export type Point = { x: number; y: number };

export type ViewportState = {
  panX: number;
  panY: number;
  zoom: number;
};

export type Size = { width: number; height: number };

/** Fit template frame into viewport; pan/zoom are view-only session state. */
export function fitFrameToViewport(frame: Size, viewport: Size, padding = 0): ViewportState {
  const innerWidth = Math.max(1, viewport.width - padding * 2);
  const innerHeight = Math.max(1, viewport.height - padding * 2);
  const zoom = Math.min(innerWidth / frame.width, innerHeight / frame.height);
  const panX = padding + (innerWidth - frame.width * zoom) / 2;
  const panY = padding + (innerHeight - frame.height * zoom) / 2;

  return { panX, panY, zoom };
}

export function screenToStage(point: Point, viewport: ViewportState): Point {
  return {
    x: (point.x - viewport.panX) / viewport.zoom,
    y: (point.y - viewport.panY) / viewport.zoom,
  };
}

export function stageToScreen(point: Point, viewport: ViewportState): Point {
  return {
    x: point.x * viewport.zoom + viewport.panX,
    y: point.y * viewport.zoom + viewport.panY,
  };
}

export function zoomAtPoint(
  viewport: ViewportState,
  screenPoint: Point,
  nextZoom: number,
): ViewportState {
  const stageAnchor = screenToStage(screenPoint, viewport);
  return {
    zoom: nextZoom,
    panX: screenPoint.x - stageAnchor.x * nextZoom,
    panY: screenPoint.y - stageAnchor.y * nextZoom,
  };
}

export function panBy(viewport: ViewportState, screenDelta: Point): ViewportState {
  return {
    ...viewport,
    panX: viewport.panX + screenDelta.x,
    panY: viewport.panY + screenDelta.y,
  };
}

/** Pointer movement in screen space → document/template delta (independent of pan). */
export function screenDeltaToStageDelta(screenDelta: Point, zoom: number): Point {
  return { x: screenDelta.x / zoom, y: screenDelta.y / zoom };
}

/** Refit after viewport resize; document coordinates unchanged. */
export function refitViewport(frame: Size, nextViewport: Size, padding = 0): ViewportState {
  return fitFrameToViewport(frame, nextViewport, padding);
}

/** Keep zoom; re-center frame when the stage container resizes. */
export function recenterViewportPreservingZoom(
  frame: Size,
  stage: Size,
  zoom: number,
  padding = 0,
): ViewportState {
  const innerWidth = Math.max(1, stage.width - padding * 2);
  const innerHeight = Math.max(1, stage.height - padding * 2);
  return {
    zoom,
    panX: padding + (innerWidth - frame.width * zoom) / 2,
    panY: padding + (innerHeight - frame.height * zoom) / 2,
  };
}

function isUsableStageSize(stage: Size): boolean {
  return stage.width > 0 && stage.height > 0;
}

/**
 * Fit the first time the stage has a real size (fill-container measure).
 * Later resizes keep the current zoom so a window change does not jump scale.
 */
export function viewportAfterStageSizeChange(
  previousStage: Size,
  nextStage: Size,
  frame: Size,
  currentZoom: number,
  padding = 0,
): ViewportState | null {
  if (!isUsableStageSize(nextStage)) {
    return null;
  }
  if (!isUsableStageSize(previousStage)) {
    return fitFrameToViewport(frame, nextStage, padding);
  }
  return recenterViewportPreservingZoom(frame, nextStage, currentZoom, padding);
}
